import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Switch,
    IconButton,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
    Slider,
    Stack,
    Divider,
    Snackbar,
    InputAdornment,
    Stepper,
    Step,
    StepLabel,
    Grid
} from '@mui/material';
import {
    Add, Delete, List as ListIcon, Map as MapIcon,
    Search, CheckCircle, Explore, Payments, Info, ChevronRight, ChevronLeft, Edit
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { customModal } from './CustomModal';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ZoneManagement = () => {
    const { t } = useTranslation();
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [viewMode, setViewMode] = useState('table');
    const [activeStep, setActiveStep] = useState(0);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditPricing, setOpenEditPricing] = useState(false);
    const [selectedZoneForEdit, setSelectedZoneForEdit] = useState(null);
    const [editPricing, setEditPricing] = useState({ truck: 0, tractor: 0 });
    const [savingPricing, setSavingPricing] = useState(false);
    const [newZone, setNewZone] = useState({
        name: '',
        state: '',
        city: '',
        description: '',
        latitude: 10.787,
        longitude: 79.623,
        radius: 5,
        isActive: true,
        pricing: {
            truck: 1200,
            tractor: 800
        }
    });

    const fetchZones = useCallback(async () => {
        try {
            const response = await axios.get('/admin/zones');
            setZones(response.data);
        } catch (err) {
            setError(err.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchZones();
    }, [fetchZones]);

    useEffect(() => {
        if (!openAddDialog) {
            setActiveStep(0);
        }
    }, [openAddDialog]);

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            await axios.put(`/admin/zones/${id}/status`, {
                isActive: !currentStatus
            });
            fetchZones();
            showNotification(t('statusUpdated') || 'Status updated successfully');
        } catch (err) {
            setError(err.response?.data?.message || t('error'));
        }
    };

    const handleDeleteZone = async (id) => {
        const confirmed = await customModal.confirm(t('deleteZone') || "Delete Zone", t('confirmDeleteZone') || 'Are you sure you want to delete this zone?', { type: 'error', confirmText: 'Delete' });
        if (confirmed) {
            try {
                await axios.delete(`/admin/zones/${id}`);
                fetchZones();
                showNotification(t('zoneDeleted') || 'Zone deleted successfully');
            } catch (err) {
                setError(err.response?.data?.message || t('error'));
            }
        }
    };

    const handleAddZone = async () => {
        try {
            await axios.post('/admin/zones', newZone);
            fetchZones();
            setOpenAddDialog(false);
            showNotification(t('zoneAdded') || 'New service zone created successfully!');
            setNewZone({
                name: '',
                state: '',
                city: '',
                description: '',
                latitude: 10.787,
                longitude: 79.623,
                radius: 5,
                isActive: true,
                pricing: {
                    truck: 1200,
                    tractor: 800
                }
            });
        } catch (err) {
            setError(err.response?.data?.message || t('error'));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewZone(prev => ({
            ...prev,
            [name]: (name === 'latitude' || name === 'longitude' || name === 'radius') ? parseFloat(value || 0) : value
        }));
    };

    const handlePresetRadius = (type) => {
        const radii = { city: 50, state: 500, region: 150 };
        setNewZone(prev => ({ ...prev, radius: radii[type] || 5 }));
    };

    const handleEditPricing = (zone) => {
        setSelectedZoneForEdit(zone);
        setEditPricing({
            truck: zone.pricing?.truck || 0,
            tractor: zone.pricing?.tractor || 0
        });
        setOpenEditPricing(true);
    };

    const handleSavePricing = async () => {
        if (!selectedZoneForEdit) return;
        setSavingPricing(true);
        try {
            await axios.put(`/admin/zones/${selectedZoneForEdit._id}/pricing`, {
                pricing: {
                    truck: Number(editPricing.truck) || 0,
                    tractor: Number(editPricing.tractor) || 0
                }
            });
            fetchZones();
            setOpenEditPricing(false);
            showNotification('Zone pricing updated successfully!');
        } catch (err) {
            setError(err.response?.data?.message || t('error'));
        } finally {
            setSavingPricing(false);
        }
    };

    const filteredZones = zones.filter(zone =>
        zone.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        zone.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        zone.state.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const LocationPicker = () => {
        useMapEvents({
            click(e) {
                setNewZone(prev => ({
                    ...prev,
                    latitude: e.latlng.lat,
                    longitude: e.latlng.lng
                }));
            },
        });
        return newZone.latitude ? (
            <Marker position={[newZone.latitude, newZone.longitude]} />
        ) : null;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '70vh', alignItems: 'center' }}>
                <CircularProgress size={60} thickness={4} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, backgroundColor: '#f8fafc', minHeight: '90vh' }}>
            {/* Header Section */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 4, backgroundColor: 'white', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'center' }, gap: 3 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                            {t('zoneManagement') || 'Service Zones'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {t('manageAreasDescription') || 'Configure operating regions and regional pricing'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <TextField
                            size="small"
                            placeholder={t('searchZones') || 'Search name, city...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                backgroundColor: '#f1f5f9',
                                borderRadius: 3,
                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                width: { xs: '100%', sm: 300 }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: '#64748b' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, next) => next && setViewMode(next)}
                            size="small"
                        >
                            <ToggleButton value="table" sx={{ px: 2 }}>
                                <ListIcon sx={{ mr: 1, fontSize: 20 }} /> {t('table') || 'List'}
                            </ToggleButton>
                            <ToggleButton value="map" sx={{ px: 2 }}>
                                <MapIcon sx={{ mr: 1, fontSize: 20 }} /> {t('map') || 'Map'}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setOpenAddDialog(true)}
                            sx={{
                                borderRadius: 3,
                                px: 4,
                                py: 1.2,
                                textTransform: 'none',
                                fontWeight: 700,
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                                backgroundColor: '#2563eb',
                                '&:hover': { backgroundColor: '#1d4ed8' }
                            }}
                        >
                            {t('addZone') || 'Create Zone'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {viewMode === 'table' ? (
                <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', py: 2 }}>{t('zoneName') || 'Zone Name'}</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>{t('location') || 'Location'}</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>{t('coordinates') || 'Radius / Coordinates'}</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>{t('basePricing') || 'Base Pricing'}</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }} align="center">{t('status') || 'Status'}</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }} align="right">{t('actions') || 'Actions'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredZones.map((zone) => (
                                <TableRow key={zone._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{zone.name}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Chip label={zone.state} size="small" sx={{ fontWeight: 600, backgroundColor: '#eff6ff', color: '#1d4ed8' }} />
                                            <Chip label={zone.city} size="small" variant="outlined" sx={{ fontWeight: 600, color: '#64748b' }} />
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>{zone.radius || 0} km radius</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {zone.latitude?.toFixed?.(4)}, {zone.longitude?.toFixed?.(4)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ borderLeft: '3px solid #2563eb', pl: 1, py: 0.5 }}>
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: '#64748b' }}>
                                                Lorry: <span style={{ color: '#0f172a' }}>₹{zone.pricing?.truck || 0}</span>
                                            </Typography>
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: '#64748b' }}>
                                                Tractor: <span style={{ color: '#0f172a' }}>₹{zone.pricing?.tractor || 0}</span>
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch
                                            checked={zone.isActive}
                                            onChange={() => handleToggleStatus(zone._id, zone.isActive)}
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleEditPricing(zone)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#eff6ff',
                                                    '&:hover': { backgroundColor: '#dbeafe' },
                                                    borderRadius: 2
                                                }}
                                                title="Edit Pricing"
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDeleteZone(zone._id)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#fef2f2',
                                                    '&:hover': { backgroundColor: '#fee2e2' },
                                                    borderRadius: 2
                                                }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredZones.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                                            {searchTerm ? t('noMatches') || 'No matching zones found' : t('noZones') || 'No service zones yet'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {searchTerm ? t('tryDifferentSearch') || 'Try adjusting your search terms' : t('getStartedByAdding') || 'Get started by clicking the "Create Zone" button'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Paper sx={{ borderRadius: 4, overflow: 'hidden', height: '650px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', border: '1px solid #e2e8f0' }}>
                    <MapContainer center={[10.787, 79.623]} zoom={7} style={{ height: '100%', width: '100%' }}>
                        <TileLayer 
                            url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
                            attribution='&copy; OpenStreetMap France | &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            maxZoom={20}
                            maxNativeZoom={19}
                        />
                        {filteredZones.filter(z => z.latitude && z.longitude).map(zone => (
                            <React.Fragment key={zone._id}>
                                <Marker position={[zone.latitude, zone.longitude]}>
                                    <Popup>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{zone.name}</Typography>
                                        <Typography variant="caption" display="block" color="text.secondary">{zone.city}, {zone.state}</Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="caption" display="block"><b>Lorry Rate:</b> ₹{zone.pricing?.truck}</Typography>
                                        <Typography variant="caption" display="block"><b>Tractor Rate:</b> ₹{zone.pricing?.tractor}</Typography>
                                    </Popup>
                                </Marker>
                                <Circle
                                    center={[zone.latitude, zone.longitude]}
                                    radius={(zone.radius || 5) * 1000}
                                    pathOptions={{
                                        color: zone.isActive ? '#2563eb' : '#94a3b8',
                                        fillColor: zone.isActive ? '#2563eb' : '#94a3b8',
                                        fillOpacity: 0.15,
                                        weight: 2
                                    }}
                                />
                            </React.Fragment>
                        ))}
                    </MapContainer>
                </Paper>
            )}

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                    severity={notification.severity}
                    variant="filled"
                    icon={<CheckCircle fontSize="inherit" />}
                    sx={{ borderRadius: 3, boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontWeight: 600 }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>

            {/* Add Zone Dialog - Stepper Wizard */}
            <Dialog
                open={openAddDialog}
                onClose={() => setOpenAddDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
            >
                <Box sx={{ p: 4, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 3 }}>
                        {t('addNewServiceZone') || 'Deploy New Service Area'}
                    </Typography>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {['Identity', 'Reach', 'Financials'].map((label) => (
                            <Step key={label}>
                                <StepLabel>{t(label.toLowerCase()) || label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <DialogContent sx={{ p: 4, minHeight: '400px' }}>
                    {activeStep === 0 && (
                        <Box sx={{ py: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Info color="primary" /> {t('basicInformation') || 'General Details'}
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={t('zoneName') || 'Zone Name (e.g., Chennai North)'}
                                        name="name"
                                        value={newZone.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Give this zone a unique name"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label={t('state') || 'State'}
                                        name="state"
                                        value={newZone.state}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label={t('city') || 'City'}
                                        name="city"
                                        value={newZone.city}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={t('description') || 'Description'}
                                        name="description"
                                        multiline
                                        rows={3}
                                        value={newZone.description}
                                        onChange={handleInputChange}
                                        placeholder="Internal notes about this region..."
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {activeStep === 1 && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Explore color="primary" /> {t('coverageMap') || 'Coverage & Location'}
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={7}>
                                    <Box sx={{ height: '400px', borderRadius: 4, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
                                        <MapContainer center={[newZone.latitude, newZone.longitude]} zoom={10} style={{ height: '100%' }}>
                                            <TileLayer 
                                             url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
                                             attribution='&copy; OpenStreetMap France | &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                             maxZoom={20}
                                             maxNativeZoom={19}
                                         />
                                            <LocationPicker />
                                            <Circle
                                                center={[newZone.latitude, newZone.longitude]}
                                                radius={newZone.radius * 1000}
                                                pathOptions={{ color: '#2563eb', fillColor: '#2563eb', fillOpacity: 0.2, weight: 2 }}
                                            />
                                        </MapContainer>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                        {t('mapHint') || '💡 Click anywhere on the map to set the zone center'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <Box sx={{ p: 2, bgcolor: '#f1f5f9', borderRadius: 3, mb: 3 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>COVERAGE RADIUS (KM)</Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2563eb', mb: 2 }}>{newZone.radius} km</Typography>
                                        <Slider
                                            value={newZone.radius}
                                            onChange={(e, val) => setNewZone(prev => ({ ...prev, radius: val }))}
                                            min={1}
                                            max={500}
                                            sx={{ mb: 2 }}
                                        />
                                        <Stack direction="row" flexWrap="wrap" gap={1}>
                                            <Button size="small" variant="contained" disableElevation onClick={() => handlePresetRadius('city')} sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#fff', color: '#1e293b', '&:hover': { bgcolor: '#e2e8f0' } }}>City (50km)</Button>
                                            <Button size="small" variant="contained" disableElevation onClick={() => handlePresetRadius('region')} sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#fff', color: '#1e293b', '&:hover': { bgcolor: '#e2e8f0' } }}>Region (150km)</Button>
                                            <Button size="small" variant="contained" disableElevation onClick={() => handlePresetRadius('state')} sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#fff', color: '#1e293b', '&:hover': { bgcolor: '#e2e8f0' } }}>State (500km)</Button>
                                        </Stack>
                                    </Box>
                                    <Stack spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Latitude"
                                            size="small"
                                            type="number"
                                            value={newZone.latitude}
                                            onChange={handleInputChange}
                                            name="latitude"
                                        />
                                        <TextField
                                            fullWidth
                                            label="Longitude"
                                            size="small"
                                            type="number"
                                            value={newZone.longitude}
                                            onChange={handleInputChange}
                                            name="longitude"
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {activeStep === 2 && (
                        <Box sx={{ py: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Payments color="primary" /> {t('regionalPricing') || 'Vehicle Service Rates'}
                            </Typography>
                            <Box sx={{ maxWidth: '500px', mx: 'auto' }}>
                                <Paper variant="outlined" sx={{ p: 4, borderRadius: 4, bgcolor: '#f8fafc', mb: 4 }}>
                                    <Stack spacing={4}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#475569' }}>LORRY (TRUCK) BASE RATE</Typography>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                type="number"
                                                value={newZone.pricing.truck}
                                                onChange={(e) => setNewZone(prev => ({
                                                    ...prev,
                                                    pricing: { ...prev.pricing, truck: parseFloat(e.target.value || 0) }
                                                }))}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#475569' }}>TRACTOR BASE RATE</Typography>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                type="number"
                                                value={newZone.pricing.tractor}
                                                onChange={(e) => setNewZone(prev => ({
                                                    ...prev,
                                                    pricing: { ...prev.pricing, tractor: parseFloat(e.target.value || 0) }
                                                }))}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                }}
                                            />
                                        </Box>
                                    </Stack>
                                </Paper>
                                <Alert severity="info" sx={{ borderRadius: 3 }}>
                                    {t('pricingNotice') || 'These rates will be applied to all service requests originating within this zone.'}
                                </Alert>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Button
                        onClick={() => activeStep === 0 ? setOpenAddDialog(false) : setActiveStep(prev => prev - 1)}
                        color="inherit"
                        startIcon={activeStep > 0 ? <ChevronLeft /> : null}
                        sx={{ fontWeight: 700 }}
                    >
                        {activeStep === 0 ? t('cancel') : t('back')}
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button
                        variant="contained"
                        onClick={() => activeStep === 2 ? handleAddZone() : setActiveStep(prev => prev + 1)}
                        endIcon={activeStep < 2 ? <ChevronRight /> : null}
                        disabled={activeStep === 0 && (!newZone.name || !newZone.state || !newZone.city)}
                        sx={{
                            borderRadius: 3,
                            px: 4,
                            py: 1.2,
                            fontWeight: 800,
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            bgcolor: '#2563eb',
                            '&:hover': { bgcolor: '#1d4ed8' }
                        }}
                    >
                        {activeStep === 2 ? t('createZone') : t('next')}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Edit Pricing Dialog */}
            <Dialog
                open={openEditPricing}
                onClose={() => !savingPricing && setOpenEditPricing(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800 }}>Edit Zone Pricing</DialogTitle>
                <DialogContent>
                    {selectedZoneForEdit && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>ZONE</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedZoneForEdit.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {selectedZoneForEdit.city}, {selectedZoneForEdit.state}
                            </Typography>
                        </Box>
                    )}
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>🚛 Lorry (Truck) Rate</Typography>
                            <TextField
                                fullWidth
                                type="number"
                                value={editPricing.truck}
                                onChange={(e) => setEditPricing(prev => ({ ...prev, truck: e.target.value }))}
                                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                inputProps={{ min: 0 }}
                                size="small"
                            />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#2e7d32' }}>🚜 Tractor Rate</Typography>
                            <TextField
                                fullWidth
                                type="number"
                                value={editPricing.tractor}
                                onChange={(e) => setEditPricing(prev => ({ ...prev, tractor: e.target.value }))}
                                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                inputProps={{ min: 0 }}
                                size="small"
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenEditPricing(false)} color="inherit" disabled={savingPricing} sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button
                        onClick={handleSavePricing}
                        variant="contained"
                        disabled={savingPricing}
                        sx={{ borderRadius: 2, px: 3, fontWeight: 700, backgroundColor: '#2563eb' }}
                    >
                        {savingPricing ? 'Saving...' : 'Save Pricing'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ZoneManagement;
